import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { SERVER_URL } from "../../constants";
import { settleWithConcurrency } from "./utils/concurrency";
import { bookingStatusName, inGroupScope, planForDestination } from "./utils/copyPlan";

// "500" tells nobody anything; the backend usually sends a message with it.
async function httpErrorText(res) {
  const text = await res.text().catch(() => "");
  let msg = "";
  try {
    const j = JSON.parse(text);
    msg = j?.message || j?.error || "";
  } catch {
    msg = text;
  }
  msg = String(msg || "").trim();
  return msg ? `${res.status} ${msg.slice(0, 160)}` : `HTTP ${res.status}`;
}

const isResidentialType = (pt) => {
  const s = String(pt ?? "").trim().toUpperCase();
  return s === "RESIDENTIAL" || s === "0";
};

// Push ONE member's in-group bookings out to many members of the same kind. The
// mirror of CopyBookingsDialog ("copy from"): same planning, but N destinations,
// and because nobody can read N grids the plan is checked for clashes and shown
// per destination BEFORE anything is written. A destination with a clash is
// left unticked; the user opts it back in deliberately.
//
// Writes go through the batch endpoints per destination (students) — one
// create, one update, one delete — capped at 4 in flight, and the outcome is
// reported per member rather than as one toast.
export default function MultiCopyBookingsDialog({
  open,
  onClose,
  token,
  showToast,
  source, // { kind, id, key, name }
  candidates = [], // same-kind people [{ kind, id, key, name }]
  blocksByKey = {},
  recordsByKey = {},
  groupStart = "",
  groupEnd = "",
  groupCampus = "",
  showFinancials = false,
  onApplied, // async (people[]) => void — refresh the destinations
}) {
  const [mode, setMode] = useState("apply"); // "apply" | "match"
  const [pickedBookings, setPickedBookings] = useState(new Set()); // source booking ids
  const [pickedDests, setPickedDests] = useState(new Set()); // dest keys
  const [touchedDests, setTouchedDests] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null); // [{ person, status, reason }]

  const scope = useMemo(
    () => ({ groupStart, groupEnd, groupCampus }),
    [groupStart, groupEnd, groupCampus]
  );

  // The source's in-group bookings — what's on offer to push.
  const sourceBlocks = useMemo(
    () => (source ? (blocksByKey[source.key] || []).filter((b) => inGroupScope(b, scope)) : []),
    [source, blocksByKey, scope]
  );

  useEffect(() => {
    if (!open) return;
    setMode("apply");
    setPickedBookings(new Set(sourceBlocks.map((b) => b.id)));
    setTouchedDests(false);
    setApplying(false);
    setProgress(0);
    setResults(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.key]);

  const chosenSource = useMemo(
    () => sourceBlocks.filter((b) => pickedBookings.has(b.id)),
    [sourceBlocks, pickedBookings]
  );

  // One plan per candidate, recomputed as the mode / chosen bookings change.
  const rows = useMemo(
    () =>
      candidates.map((p) => ({
        person: p,
        ...planForDestination({
          srcBlocks: chosenSource,
          dstBlocks: blocksByKey[p.key] || [],
          destRecords: recordsByKey[p.key] || [],
          scope,
          mode,
        }),
      })),
    [candidates, chosenSource, blocksByKey, recordsByKey, scope, mode]
  );

  // Default selection: everyone with something to do and no clash. Re-derived
  // whenever the plan changes until the user touches the checkboxes themselves.
  useEffect(() => {
    if (!open || touchedDests) return;
    setPickedDests(
      new Set(rows.filter((r) => r.changes > 0 && r.conflicts.length === 0).map((r) => r.person.key))
    );
  }, [open, rows, touchedDests]);

  const selectable = rows.filter((r) => r.changes > 0);
  const selectedRows = rows.filter((r) => pickedDests.has(r.person.key) && r.changes > 0);
  const conflictCount = rows.filter((r) => r.conflicts.length > 0).length;

  const toggleDest = (key) => {
    setTouchedDests(true);
    setPickedDests((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const setAllDests = (on) => {
    setTouchedDests(true);
    setPickedDests(on ? new Set(selectable.map((r) => r.person.key)) : new Set());
  };
  const toggleBooking = (id) =>
    setPickedBookings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // --- writes -------------------------------------------------------------------
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const post = async (url, body) => {
    const res = await fetch(url, { method: "POST", headers: h, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(await httpErrorText(res));
    return res.json().catch(() => null);
  };

  // Remove → adjust → add. Removing first means the destination is never holding
  // its outgoing bookings AND the incoming ones at once; a failure part-way then
  // leaves fewer bookings rather than more, which is the safer half-state. Each
  // step names itself so a per-member failure says which write went wrong.
  const applyToStudent = async ({ person, plan }) => {
    if (plan.toRemove.length) {
      await post(`${SERVER_URL}api/bookings/batch-delete`, { ids: plan.toRemove.map((b) => b.id) })
        .catch((e) => { throw new Error(`remove: ${e.message}`); });
    }
    if (plan.toAdjust.length) {
      const updates = plan.toAdjust.map((a) => ({
        id: a.dest.id,
        actualCharge: Number(a.src.actualCharge) || 0,
        customStart: a.customStart,
        customEnd: a.customEnd,
      }));
      const r = await post(`${SERVER_URL}api/bookings/batch-update`, { updates })
        .catch((e) => { throw new Error(`adjust: ${e.message}`); });
      if (r && Number(r.updated) !== updates.length) {
        throw new Error(`adjust: updated ${r.updated ?? 0} of ${updates.length}`);
      }
    }
    if (plan.toAdd.length) {
      const bookings = plan.toAdd.map((a) => ({
        studentId: person.id,
        productId: a.src.productId,
        actualCharge: Number(a.src.actualCharge) || 0,
        bookingStatus: bookingStatusName(a.src.raw?.bookingStatus),
        customStart: a.customStart,
        customEnd: a.customEnd,
      }));
      const r = await post(`${SERVER_URL}api/bookings/batch-create`, { bookings })
        .catch((e) => { throw new Error(`add: ${e.message}`); });
      if (r && Number(r.created) !== bookings.length) {
        throw new Error(
          `add: created ${r.created ?? 0} of ${bookings.length}${r.warnings?.length ? ` — ${r.warnings.join("; ")}` : ""}`
        );
      }
    }
  };

  // Adults have no batch endpoints — one call per booking, as the one→one copy does.
  const applyToAdult = async ({ person, plan }) => {
    for (const b of plan.toRemove) {
      const res = await fetch(`${SERVER_URL}api/adult-bookings/${b.id}`, { method: "DELETE", headers: h });
      if (!res.ok) throw new Error(`remove ${b.productName} → ${res.status}`);
    }
    for (const a of plan.toAdjust) {
      const r = a.dest.raw || {};
      const res = await fetch(`${SERVER_URL}api/adult-bookings/${a.dest.id}`, {
        method: "PUT",
        headers: h,
        body: JSON.stringify({
          participationType: r.participationType || "SUPERVISE_ONLY",
          countsForTransport: r.countsForTransport ?? true,
          countsForRatio: r.countsForRatio ?? false,
          notes: r.notes ?? "",
          customStart: a.customStart,
          customEnd: a.customEnd,
          actualCharge: Number(a.src.actualCharge) || 0,
        }),
      });
      if (!res.ok) throw new Error(`adjust ${a.src.productName} → ${res.status}`);
    }
    for (const a of plan.toAdd) {
      const b = a.src;
      const r = b.raw || {};
      await post(`${SERVER_URL}api/products/${b.productId}/adult-bookings`, {
        adultId: person.id,
        participationType:
          r.participationType || (isResidentialType(b.productType) ? "BOOKING" : "SUPERVISE_ONLY"),
        countsForTransport: r.countsForTransport ?? true,
        countsForRatio: r.countsForRatio ?? false,
        notes: r.notes ?? "",
        customStart: a.customStart,
        customEnd: a.customEnd,
        actualCharge: Number(b.actualCharge) || 0,
      });
    }
  };

  const apply = async () => {
    if (!selectedRows.length || applying) return;
    setApplying(true);
    setProgress(0);
    let done = 0;
    const settled = await settleWithConcurrency(
      selectedRows,
      async (row) => {
        try {
          if (row.person.kind === "adult") await applyToAdult(row);
          else await applyToStudent(row);
        } finally {
          done += 1;
          setProgress(done);
        }
      },
      // Three in flight, not four: the Hikari pool is five and the page keeps a
      // connection busy itself. Deadlocks are fixed server-side; this is margin.
      3
    );
    const out = settled.map((r) => ({
      person: r.item.person,
      status: r.status,
      reason: r.status === "rejected" ? String(r.reason?.message || r.reason) : null,
    }));
    setResults(out);
    setApplying(false);
    const okCount = out.filter((r) => r.status === "fulfilled").length;
    showToast?.(
      okCount === out.length
        ? `Copied to ${okCount} member${okCount === 1 ? "" : "s"}`
        : `Copied to ${okCount} of ${out.length} — see the report`,
      okCount === out.length ? "success" : "warning"
    );
    // Refresh everyone we touched, including failures (a partial batch may have landed).
    await onApplied?.(selectedRows.map((r) => r.person));
  };

  // --- render -------------------------------------------------------------------
  const money = (n) => (showFinancials ? ` £${Number(n) || 0}` : "");
  const planCell = (r) => {
    if (r.changes === 0 && !r.conflicts.length) {
      return (
        <Typography variant="body2" sx={{ opacity: 0.6 }}>
          Already matches
        </Typography>
      );
    }
    const p = r.plan;
    return (
      <Stack spacing={0.25}>
        {p.toAdd.map((a, i) => (
          <Typography key={`a${i}`} variant="body2" color="success.main">
            + {a.src.productName} ({a.customStart} → {a.customEnd}){money(a.src.actualCharge)}
          </Typography>
        ))}
        {p.toAdjust.map((a, i) => (
          <Typography key={`j${i}`} variant="body2" color="warning.main">
            ~ {a.src.productName} → ({a.customStart} → {a.customEnd}){money(a.src.actualCharge)}
          </Typography>
        ))}
        {p.toRemove.map((b, i) => (
          <Typography key={`r${i}`} variant="body2" color="error.main">
            − {b.productName} ({b.start} → {b.end})
          </Typography>
        ))}
        {p.skipped.map((s, i) => (
          <Typography key={`s${i}`} variant="caption" sx={{ opacity: 0.7 }}>
            skipped {s.src.productName} — {s.reason}
          </Typography>
        ))}
      </Stack>
    );
  };

  const resultFor = (key) => results?.find((r) => r.person.key === key);

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Copy {source?.name || "member"}&apos;s bookings to others</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Pushes the ticked bookings onto every ticked {source?.kind === "adult" ? "staff member" : "student"}.
            Each destination&apos;s plan is checked for clashes first — a member with a clash
            is unticked; tick them back in if you&apos;ve looked and it&apos;s fine. Bookings
            outside the group window are never touched.
          </Typography>

          {/* What to copy */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Bookings to copy
            </Typography>
            {sourceBlocks.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {source?.name} has no in-group bookings.
              </Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" sx={{ rowGap: 0 }}>
                {sourceBlocks.map((b) => (
                  <FormControlLabel
                    key={b.id}
                    sx={{ mr: 2 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={pickedBookings.has(b.id)}
                        onChange={() => toggleBooking(b.id)}
                        disabled={applying || !!results}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {b.productName}{" "}
                        <Typography component="span" variant="caption" sx={{ opacity: 0.6 }}>
                          {b.start}
                          {b.end !== b.start ? ` → ${b.end}` : ""}
                          {money(b.actualCharge)}
                        </Typography>
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* How */}
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel
              value="apply"
              control={<Radio size="small" disabled={applying || !!results} />}
              label={
                <Typography variant="body2">
                  Add &amp; update — destinations keep anything extra they have
                </Typography>
              }
            />
            <FormControlLabel
              value="match"
              control={<Radio size="small" disabled={applying || !!results} />}
              label={
                <Typography variant="body2">
                  Match exactly — also remove bookings the source doesn&apos;t have
                </Typography>
              }
            />
          </RadioGroup>

          {conflictCount > 0 && !results ? (
            <Alert severity="warning">
              {conflictCount} member{conflictCount === 1 ? " has" : "s have"} a clash and{" "}
              {conflictCount === 1 ? "is" : "are"} unticked. Hover the chip to see why.
            </Alert>
          ) : null}

          {/* Per-destination plan */}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    disabled={applying || !!results || !selectable.length}
                    checked={selectable.length > 0 && selectedRows.length === selectable.length}
                    indeterminate={selectedRows.length > 0 && selectedRows.length < selectable.length}
                    onChange={(e) => setAllDests(e.target.checked)}
                  />
                </TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Check</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const key = r.person.key;
                const res = resultFor(key);
                const disabled = applying || !!results || r.changes === 0;
                return (
                  <TableRow key={key} sx={disabled && !res ? { opacity: 0.7 } : undefined}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={pickedDests.has(key) && r.changes > 0}
                        disabled={disabled}
                        onChange={() => toggleDest(key)}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>{r.person.name}</TableCell>
                    <TableCell>{planCell(r)}</TableCell>
                    <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {res ? (
                        res.status === "fulfilled" ? (
                          <Chip size="small" color="success" label="Copied" />
                        ) : (
                          <Tooltip title={res.reason || ""}>
                            <Chip size="small" color="error" label="Failed" />
                          </Tooltip>
                        )
                      ) : r.conflicts.length ? (
                        <Tooltip
                          title={
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                              {r.conflicts.map((c, i) => (
                                <li key={i}>{c}</li>
                              ))}
                            </Box>
                          }
                        >
                          <Chip size="small" color="warning" variant="outlined" label="Clash" />
                        </Tooltip>
                      ) : r.changes > 0 ? (
                        <Chip size="small" variant="outlined" label="OK" />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Nobody else of the same kind in this group.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {applying ? (
            <LinearProgress
              variant="determinate"
              value={selectedRows.length ? (progress / selectedRows.length) * 100 : 0}
            />
          ) : null}

          {results ? (
            <Alert severity={results.every((r) => r.status === "fulfilled") ? "success" : "warning"}>
              {results.filter((r) => r.status === "fulfilled").length} of {results.length} copied.
              {results.some((r) => r.status === "rejected") ? (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {results
                    .filter((r) => r.status === "rejected")
                    .map((r) => (
                      <li key={r.person.key}>
                        {r.person.name}: {r.reason}
                      </li>
                    ))}
                </Box>
              ) : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          {results ? "Close" : "Cancel"}
        </Button>
        {!results && (
          <Button
            variant="contained"
            onClick={apply}
            disabled={applying || !selectedRows.length || !chosenSource.length}
          >
            {applying
              ? `Copying… ${progress}/${selectedRows.length}`
              : `Copy to ${selectedRows.length} member${selectedRows.length === 1 ? "" : "s"}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
